import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Loader2 } from 'lucide-react';
import { Dialog, DialogDescription, DialogTitle } from 'components/ui/dialog';
import { PublicDialogContent } from 'components/ui/public-dialog';
import { FlowButton } from 'components/ui/flow-button';
import { EQUIPMENT, STATE_EDITIONS } from 'data/paperworkVariants';
import { extractCarrierFlowReference, submitCompanyApplication } from 'lib/sitePublicApi';
import './PaperworkFinder.css';

const STEPS = ['Company & contact', 'Your operation', 'Current paperwork', 'Your priorities', 'Review & submit'];
const FLEET_SIZES = [['5', '1-5 trucks'], ['15', '6-15 trucks'], ['50', '16-50 trucks'], ['100', '51-100 trucks'], ['101', '100+ trucks']];
const OPERATIONS = ['Dry van', 'Reefer', 'Flatbed', 'Intermodal', 'Car hauling', 'Local / regional', 'OTR', 'Mixed operation', 'Other'].map(label => [label, label]);
const ATTENTION_AREAS = ['Driver files', 'Compliance folders', 'DOT audit readiness', 'Safety meetings', 'Incident review', 'Inspection follow-up / DataQs', 'Driver onboarding', 'Training', 'Recruiting', 'TMS workflows', 'SOPs', 'Dashboards / management visibility', 'Other'];
const URGENCIES = ['Immediately', 'Within 30 days', 'Within 60-90 days', 'Planning ahead', 'Not sure yet'].map(label => [label, label]);
const ARRANGEMENTS = [['w2', 'W-2 employees'], ['owner-operator', 'Owner-operators'], ['both', 'Both arrangements'], ['not-sure', 'Not sure yet']];
const EQUIPMENT_OPTIONS = [...EQUIPMENT.map(item => [item.id, item.label]), ['other', 'Other equipment'], ['not-sure', 'Not sure yet']];
const STATE_OPTIONS = [...STATE_EDITIONS.map(item => [item.code, item.name]), ['not-sure', 'Not sure / need help choosing']];
const REQUIRED_BY_STEP = [
  ['companyName', 'contactName', 'contactTitle', 'email', 'phone', 'companyAddress', 'companyZipCode'],
  ['fleetSize', 'operationType', 'workingArrangement', 'equipmentType', 'stateEdition'],
  ['existingPaperwork'],
  ['painPoints', 'urgency'],
];
const TITLES = [
  <>Start with <em>your company.</em></>,
  <>Tell us about <em>your operation.</em></>,
  <>What paperwork <em>do you have?</em></>,
  <>What needs <em>attention first?</em></>,
  <>Your answers. <em>Your next step.</em></>,
];
const DESCRIPTIONS = [
  'Share your company and contact details. Fields marked * are required.',
  'These details help us understand the paperwork that may fit your operation. Enter a DOT number or MC number.',
  'Tell us what you already use, then select the areas where you need help. Do not include driver records or other sensitive personal information.',
  'Explain your main concern, what you have tried and when you would like to address it.',
  'Check your answers before sending them to Magellan. You can edit any section. This questionnaire is free and does not place an order.',
];
const initialForm = () => ({
  companyName: '', contactName: '', contactTitle: '', email: '', phone: '', website: '',
  companyAddress: '', companyZipCode: '', dotNumber: '', mcNumber: '', fleetSize: '', operationType: '',
  workingArrangement: '', equipmentType: '', stateEdition: '', existingPaperwork: '',
  areasNeedAttention: [], painPoints: '', currentSystems: '', urgency: '', additionalNotes: '', consentAccepted: false,
});
const number = index => String(index + 1).padStart(2, '0');
const optionLabel = (options, value) => options.find(([id]) => id === value)?.[1] || value;

function Field({ field, label, form, update, idPrefix, required = false, options, multiline = false, maxLength = 10000, ...props }) {
  const id = `${idPrefix}-${field}`;
  const Component = options ? 'select' : multiline ? 'textarea' : 'input';
  return <div className={'rf-field' + (multiline ? ' rf-field-wide' : '')}>
    <label htmlFor={id}>{label}{required && <span aria-hidden="true"> *</span>}</label>
    <Component {...props} id={id} name={field} data-testid={'finder-' + field} required={required}
      {...(!options ? { maxLength } : {})} {...(multiline ? { rows: 4 } : {})}
      value={form[field]} onChange={event => { event.target.setCustomValidity(''); update(field, event.target.value); }}>
      {options && <><option value="">Select one</option>{options.map(([value, text]) => <option key={value} value={value}>{text}</option>)}</>}
    </Component>
  </div>;
}

export default function PaperworkFinder() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState(null);
  const headingRef = useRef(null);
  const bodyRef = useRef(null);
  const formRef = useRef(null);
  const errorRef = useRef(null);
  const openerRef = useRef(null);
  const idPrefix = useId();
  const formId = idPrefix + '-form';

  useEffect(() => {
    if (!open) return undefined;
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    const frame = requestAnimationFrame(() => headingRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, [open, step, receipt]);

  function update(field, value) {
    setForm(current => ({ ...current, [field]: value }));
    setError('');
    if (field === 'dotNumber' || field === 'mcNumber') formRef.current?.elements.namedItem('dotNumber')?.setCustomValidity('');
    if (field === 'areasNeedAttention') formRef.current?.querySelector('[name="areasNeedAttention"]')?.setCustomValidity('');
  }
  function changeStep(index) {
    setError('');
    setStep(index);
  }
  function setValidationMessage(control, message) {
    if (control) {
      control.setCustomValidity(message);
      control.reportValidity();
      control.focus();
    }
  }
  function validateStep() {
    const element = formRef.current;
    if (!element) return false;
    for (const field of REQUIRED_BY_STEP[step] || []) {
      const control = element.elements.namedItem(field);
      if (control && !String(form[field]).trim()) {
        setValidationMessage(control, 'Please complete this field.');
        return false;
      }
    }
    if (step === 1 && !form.dotNumber.trim() && !form.mcNumber.trim()) {
      setValidationMessage(element.elements.namedItem('dotNumber'), 'Enter a DOT number or MC number.');
      return false;
    }
    if (step === 2 && !form.areasNeedAttention.length) {
      setValidationMessage(element.querySelector('[name="areasNeedAttention"]'), 'Select at least one area that needs attention.');
      return false;
    }
    return element.reportValidity();
  }
  function firstIncompleteStep() {
    const missing = REQUIRED_BY_STEP.findIndex(fields => fields.some(field => !String(form[field]).trim()));
    if (missing >= 0) return missing;
    if (!form.dotNumber.trim() && !form.mcNumber.trim()) return 1;
    if (!form.areasNeedAttention.length) return 2;
    return -1;
  }
  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting || receipt || !validateStep()) return;
    if (step < 4) {
      changeStep(step + 1);
      return;
    }
    const incomplete = firstIncompleteStep();
    if (incomplete !== -1) {
      changeStep(incomplete);
      return;
    }
    if (!form.consentAccepted) return;
    setSubmitting(true);
    setError('');
    const { workingArrangement, equipmentType, stateEdition, existingPaperwork, consentAccepted, ...legacy } = form;
    const payload = Object.fromEntries(Object.entries(legacy).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value]));
    payload.dotMc = '';
    payload.freightType = payload.operationType;
    payload.interest = payload.areasNeedAttention.join(', ');
    payload.paperworkFinder = { version: 1, workingArrangement, equipmentType, stateEdition, existingPaperwork: existingPaperwork.trim(), consentAccepted };
    try {
      const result = await submitCompanyApplication(payload);
      if (result.success) setReceipt({ reference: extractCarrierFlowReference(result.data) });
      else {
        setError(result.error || 'Your answers could not be sent. Please try again.');
        requestAnimationFrame(() => errorRef.current?.focus());
      }
    } catch {
      setError('Your answers could not be sent. Please try again.');
      requestAnimationFrame(() => errorRef.current?.focus());
    } finally {
      setSubmitting(false);
    }
  }

  const sharedField = { form, update, idPrefix };
  const reviewGroups = [
    { title: 'Company & contact', rows: [
      ['Company name', form.companyName], ['Your name', form.contactName], ['Your role', form.contactTitle],
      ['Email address', form.email], ['Phone number', form.phone], ['Company website', form.website],
      ['Company address', form.companyAddress], ['Company ZIP code', form.companyZipCode],
    ] },
    { title: 'Your operation', rows: [
      ['DOT Number', form.dotNumber], ['MC Number', form.mcNumber], ['Fleet size', optionLabel(FLEET_SIZES, form.fleetSize)],
      ['Primary operation type', form.operationType], ['Working arrangement', optionLabel(ARRANGEMENTS, form.workingArrangement)],
      ['Equipment type', optionLabel(EQUIPMENT_OPTIONS, form.equipmentType)], ['State edition', optionLabel(STATE_OPTIONS, form.stateEdition)],
    ] },
    { title: 'Current paperwork', rows: [
      ['What paperwork do you already use?', form.existingPaperwork], ['What areas need attention?', form.areasNeedAttention.join(', ')],
    ] },
    { title: 'Your priorities', rows: [
      ['What is the biggest problem right now?', form.painPoints], ['What have you already tried?', form.currentSystems],
      ['How soon do you want to address this?', form.urgency], ['Anything else we should know?', form.additionalNotes],
    ] },
  ];

  return <>
    <section className="rf-introduction" data-testid="paperwork-finder-section" aria-labelledby={idPrefix + '-introduction'}>
      <h3 id={idPrefix + '-introduction'} className="rf-introduction-heading">Not sure where to start?</h3>
      <div className="rf-introduction-content">
        <p>Tell us how your company operates, what you already have in place and where you need support. Magellan will review your answers and help you decide what to address first, including whether a paperwork package fits your needs.</p>
        <div><FlowButton ref={openerRef} type="button" data-testid="paperwork-finder-open" onClick={() => setOpen(true)}>Start a quick assessment</FlowButton>
          <p className="rf-introduction-note">Free to complete. No purchase required.</p></div>
      </div>
    </section>
    <Dialog open={open} onOpenChange={next => { if (!submitting) setOpen(next); }}>
      <PublicDialogContent className="resource-paperwork-finder" data-testid="paperwork-finder-dialog" data-submitting={submitting || undefined}
        onOpenAutoFocus={event => { event.preventDefault(); headingRef.current?.focus({ preventScroll: true }); }}
        onCloseAutoFocus={event => { event.preventDefault(); openerRef.current?.focus({ preventScroll: true }); }}
        onEscapeKeyDown={event => { if (submitting) event.preventDefault(); }}
        onPointerDownOutside={event => { if (submitting) event.preventDefault(); }}>
        <header className="rf-header">
          <div className="rf-kicker"><span>Quick assessment</span><span>{receipt ? 'Received' : number(step) + ' / 05'}</span></div>
          <DialogTitle ref={headingRef} tabIndex={-1} className="rf-title">{receipt ? <>Your answers <em>have been received.</em></> : TITLES[step]}</DialogTitle>
          <DialogDescription className="rf-description">{receipt ? 'Thank you for telling us about your operation. Your answers have been sent to Magellan for review.' : DESCRIPTIONS[step]}</DialogDescription>
          {!receipt && <ol className="rf-progress" aria-label="Questionnaire progress">{STEPS.map((label, index) => <li key={label} aria-label={label} className={index <= step ? 'is-current' : ''} aria-current={index === step ? 'step' : undefined}><span className="rf-progress-line" /><span>{label}</span></li>)}</ol>}
        </header>
        <div ref={bodyRef} className="rf-body">
          {receipt ? <div className="rf-success" data-testid="paperwork-finder-success" role="status">
            <span className="rf-success-check" aria-hidden="true"><Check size={30} /></span>
            {receipt.reference && <div className="rf-reference"><span>Your reference</span><strong>{receipt.reference}</strong></div>}
            <p>No order has been placed. Any paperwork purchase or additional work is agreed separately.</p>
          </div> : <form id={formId} ref={formRef} onSubmit={handleSubmit} data-testid="paperwork-finder-form" noValidate>
            <fieldset className="rf-step" disabled={submitting}>
              <legend className="sr-only">{STEPS[step]}</legend>
              {step === 0 && <div className="rf-fields">
                <Field {...sharedField} field="companyName" label="Company name" required maxLength={300} autoComplete="organization" placeholder="Enter company name" />
                <Field {...sharedField} field="contactName" label="Your name" required maxLength={200} autoComplete="name" placeholder="Enter your full name" />
                <Field {...sharedField} field="contactTitle" label="Your role" required autoComplete="organization-title" placeholder="Owner, manager, safety, dispatch, recruiting, operations, other" />
                <Field {...sharedField} field="email" label="Email address" required type="email" maxLength={320} autoComplete="email" placeholder="Enter work email" />
                <Field {...sharedField} field="phone" label="Phone number" required type="tel" autoComplete="tel" placeholder="Enter phone number" />
                <Field {...sharedField} field="website" label="Company website" placeholder="Enter website if available" />
                <Field {...sharedField} field="companyAddress" label="Company address" required autoComplete="street-address" placeholder="Street address, city, state" />
                <Field {...sharedField} field="companyZipCode" label="Company ZIP code" required autoComplete="postal-code" placeholder="Enter ZIP code" />
              </div>}
              {step === 1 && <>
                <div className="rf-fields">
                  <Field {...sharedField} field="dotNumber" label="DOT Number" type="text" inputMode="numeric" placeholder="Enter DOT number" aria-describedby={idPrefix + '-dot-note'} />
                  <Field {...sharedField} field="mcNumber" label="MC Number" type="text" placeholder="Enter MC number if available" aria-describedby={idPrefix + '-dot-note'} />
                </div>
                <p id={idPrefix + '-dot-note'} className="rf-help">A DOT number or MC number is required. You can provide both.</p>
                <div className="rf-fields">
                  <Field {...sharedField} field="fleetSize" label="Fleet size" required options={FLEET_SIZES} />
                  <Field {...sharedField} field="operationType" label="Primary operation type" required options={OPERATIONS} />
                  <Field {...sharedField} field="workingArrangement" label="Working arrangement" required options={ARRANGEMENTS} />
                  <Field {...sharedField} field="equipmentType" label="Equipment type" required options={EQUIPMENT_OPTIONS} />
                  <Field {...sharedField} field="stateEdition" label="State edition" required options={STATE_OPTIONS} />
                </div>
                <p className="rf-help">Paperwork packages are specific to one working arrangement, equipment type and state. Choose “Not sure” if you need help identifying the right edition.</p>
              </>}
              {step === 2 && <div className="rf-step-stack">
                <Field {...sharedField} field="existingPaperwork" label="What paperwork do you already use?" required multiline maxLength={5000} placeholder="List the policies, procedures, forms or tracking files you already have. If you are starting from scratch or are not sure, tell us." />
                <fieldset className="rf-areas"><legend>What areas need attention? <span aria-hidden="true">*</span></legend>
                  <p className="rf-help">Select all that apply. At least one area is required.</p>
                  <div className="rf-checks">{ATTENTION_AREAS.map((area, index) => {
                    const checked = form.areasNeedAttention.includes(area);
                    return <label key={area} className={'rf-check' + (checked ? ' is-selected' : '')}>
                      <input type="checkbox" name="areasNeedAttention" value={area} checked={checked} data-testid={'finder-area-' + index}
                        onChange={event => { event.target.setCustomValidity(''); update('areasNeedAttention', checked ? form.areasNeedAttention.filter(value => value !== area) : [...form.areasNeedAttention, area]); }} />
                      <span>{area}</span><span className="rf-check-mark" aria-hidden="true">{checked && <Check size={13} />}</span>
                    </label>;
                  })}</div>
                </fieldset>
              </div>}
              {step === 3 && <div className="rf-step-stack">
                <Field {...sharedField} field="painPoints" label="What is the biggest problem right now?" required multiline placeholder="Tell us what is creating the most pressure inside the company." />
                <Field {...sharedField} field="currentSystems" label="What have you already tried?" multiline placeholder="Software, templates, internal process, consultant, staff changes, other." />
                <Field {...sharedField} field="urgency" label="How soon do you want to address this?" required options={URGENCIES} />
                <Field {...sharedField} field="additionalNotes" label="Anything else we should know?" multiline placeholder="Add context that would help us understand the company." />
              </div>}
              {step === 4 && <div className="rf-review">
                {reviewGroups.map((group, index) => <section className="rf-review-group" key={group.title}>
                  <div className="rf-review-heading"><h3>{group.title}</h3><button type="button" onClick={() => changeStep(index)} aria-label={'Edit ' + group.title}>Edit</button></div>
                  <dl>{group.rows.map(([label, answer]) => <div key={label}><dt>{label}</dt><dd>{answer || 'Not provided'}</dd></div>)}</dl>
                </section>)}
                <label className="rf-consent"><input type="checkbox" name="consentAccepted" required checked={form.consentAccepted} data-testid="finder-consentAccepted" onChange={event => update('consentAccepted', event.target.checked)} />
                  <span>I agree that Magellan may use these answers and contact details to review my paperwork needs and contact me about this request. I have read the <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.</span>
                </label>
                <p className="rf-help">This questionnaire does not replace the document review included in the paperwork buying process. Any package or additional work must be agreed separately.</p>
              </div>}
            </fieldset>
            {error && <div ref={errorRef} className="rf-error" role="alert" tabIndex={-1}><p>{error}</p><p>Your answers are still here. Use “Send answers” to retry.</p></div>}
          </form>}
        </div>
        <footer className="rf-footer">
          {receipt ? <FlowButton type="button" className="rf-primary" showArrow={false} onClick={() => setOpen(false)}>Close</FlowButton> : <>
            <button type="button" className="rf-back" disabled={step === 0 || submitting} data-testid="paperwork-finder-back" onClick={() => changeStep(step - 1)}><ArrowLeft size={14} aria-hidden="true" />Back</button>
            <FlowButton type="submit" form={formId} className="rf-primary" disabled={submitting} data-testid={step === 4 ? 'paperwork-finder-submit' : 'paperwork-finder-next'}>
              {submitting ? <>Sending<Loader2 className="rf-spinner" size={14} aria-hidden="true" /></> : step === 4 ? 'Send answers' : 'Next'}
            </FlowButton>
          </>}
        </footer>
      </PublicDialogContent>
    </Dialog>
  </>;
}
